const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : DATE_TIME.format(date);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : DATE_ONLY.format(date);
}

const UNITS: [limit: number, seconds: number, unit: Intl.RelativeTimeFormatUnit][] = [
  [60, 1, "second"],
  [3600, 60, "minute"],
  [86_400, 3600, "hour"],
  [2_592_000, 86_400, "day"],
  [31_536_000, 2_592_000, "month"],
  [Infinity, 31_536_000, "year"],
];

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/** "3 minutes ago" — the useful reading for health checks and deploy feeds. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return "never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "never";

  const elapsed = (date.getTime() - Date.now()) / 1000;
  const magnitude = Math.abs(elapsed);

  for (const [limit, seconds, unit] of UNITS) {
    if (magnitude < limit) return RELATIVE.format(Math.round(elapsed / seconds), unit);
  }
  return RELATIVE.format(Math.round(elapsed / 31_536_000), "year");
}

export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

const BYTES_UNITS = ["B", "KB", "MB", "GB", "TB"];

/** "1.2 GB" — an em dash for anything that was not reported. */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < BYTES_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${BYTES_UNITS[unit]}`;
}

/** `application.deploy` → `Application deploy`, for the audit log. */
export function humanizeAction(action: string): string {
  const words = action.replace(/[._]/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
